import DatePicker from 'react-date-picker';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Link,
  ModalBody,
  ModalFooter,
  Progress,
  Stack,
  Text,
  chakra
} from '@chakra-ui/react';
import {FaFileAudio} from 'react-icons/fa';
import {FiX} from 'react-icons/fi';
import {gql, useMutation} from '@apollo/client';
import {graphql, useStaticQuery} from 'gatsby';
import {useDrop} from 'react-use';

export const CREATE_SIGNED_URL = gql`
  mutation CreateSignedUrl(
    $name: String!
    $type: String!
    $size: Int!
    $isTrial: Boolean!
  ) {
    createSignedUrl(name: $name, type: $type, size: $size, isTrial: $isTrial) {
      key
      url
    }
  }
`;

export default function ProjectForm({
  isTrial,
  project,
  buttonText,
  mutation,
  mutationOptions,
  onCancel
}) {
  const [durationShown, setDurationShown] = useState(
    project ? !project.file : false
  );

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(project?.date && new Date(project.date));
  const [mutate, {client}] = useMutation(mutation, mutationOptions);

  const [file, setFile] = useState(null);
  const {over} = useDrop({
    onFiles(files) {
      const [audio] = files.filter(file => file.type.startsWith('audio/'));
      if (audio) {
        setFile(audio);
      }
    }
  });

  const canSetDuration = useMemo(() => !project?.file, [project]);

  const {config} = useStaticQuery(
    graphql`
      {
        config {
          paidProjectCost
          freeFileSizeLimit
        }
      }
    `
  );

  function handleFile(event) {
    setFile(event.target.files[0]);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const {name, duration} = event.target;
    const variables = {
      input: {
        name: name.value,
        date
      },
      isTrial
    };

    if (duration) {
      variables.input.duration = duration.valueAsNumber;
    }

    setLoading(true);

    try {
      if (file) {
        const {type, name, size} = file;
        const {data} = await client.mutate({
          mutation: CREATE_SIGNED_URL,
          variables: {
            type,
            name,
            size,
            isTrial
          }
        });

        const {url, key} = data.createSignedUrl;
        variables.file = {name};
        // TODO: avoid uploading the same file multiple times
        variables.file.key = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();

          xhr.addEventListener('load', () => resolve(key));
          xhr.addEventListener('error', event =>
            reject(event.target.statusText)
          );
          xhr.upload.addEventListener('progress', event =>
            setProgress(event.loaded / event.total)
          );

          xhr.open('PUT', url);
          xhr.send(file);
        });

        // find audio duration and save it to the project
        // https://ourcodeworld.com/articles/read/1036/how-to-retrieve-the-duration-of-a-mp3-wav-audio-file-in-the-browser-with-javascript
        variables.input.duration = await new Promise(resolve => {
          const audio = document.createElement('audio');
          audio.onloadedmetadata = () => {
            resolve(audio.duration);
            URL.revokeObjectURL(audio.src);
          };
          audio.src = URL.createObjectURL(file);
        });
      }

      await mutate({variables});
      if (!isTrial && project?.isTrial !== isTrial) {
        // if trial status changes from anything to false, it's a purchase
        window.fbq?.('track', 'Purchase', {
          currency: 'USD',
          value: config.paidProjectCost
        });
      }
    } catch (error) {
      setError(error);
      setLoading(false);
      setProgress(null);
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <ModalBody>
        <Stack spacing="4">
          {error && <Text color="red.500">{error.message}</Text>}
          <FormControl isRequired>
            <FormLabel>Project name</FormLabel>
            <Input
              defaultValue={project?.name}
              name="name"
              placeholder="David Bowie - Under Pressure"
            />
          </FormControl>
          {durationShown ? (
            <FormControl isRequired>
              <FormLabel>Duration in seconds</FormLabel>
              <Input
                type="number"
                name="duration"
                defaultValue={project?.duration || 0}
              />
              <FormHelperText>
                Have an audio file?{' '}
                <Link
                  color="indigo.500"
                  as="button"
                  onClick={() => setDurationShown(false)}
                >
                  Upload a song
                </Link>{' '}
                instead.
              </FormHelperText>
            </FormControl>
          ) : (
            <FormControl isRequired={!project}>
              <FormLabel>Audio file</FormLabel>
              {file ? (
                <>
                  <Flex align="center">
                    <Box
                      flexShrink="0"
                      as={FaFileAudio}
                      fontSize="lg"
                      color="indigo.500"
                    />
                    <Text mx="2" isTruncated>
                      {file.name}
                    </Text>
                    <IconButton
                      onClick={() => setFile(null)}
                      size="xs"
                      fontSize="lg"
                      ml="auto"
                      icon={<FiX />}
                    />
                  </Flex>
                  {Number.isFinite(progress) && (
                    <Progress
                      mt="1"
                      size="xs"
                      colorScheme="indigo"
                      value={progress * 100}
                    />
                  )}
                </>
              ) : (
                <>
                  <Center
                    h="120px"
                    px="20"
                    borderWidth="2px"
                    borderStyle="dashed"
                    rounded="lg"
                    textAlign="center"
                    borderColor={over && 'indigo.200'}
                    pos="relative"
                  >
                    <div>
                      Drop {project ? 'a new' : 'your'} audio file anywhere or
                      click here to upload.
                    </div>
                    <chakra.input
                      required={!project}
                      type="file"
                      accept="audio/*"
                      onChange={handleFile}
                      pos="absolute"
                      top="0"
                      left="0"
                      boxSize="full"
                      opacity="0"
                      cursor="pointer"
                    />
                  </Center>
                  {(isTrial || canSetDuration) && (
                    <FormHelperText>
                      {isTrial &&
                        `${config.freeFileSizeLimit} MB audio file size limit. `}
                      {canSetDuration && (
                        <>
                          If you don&apos;t have an audio file,{' '}
                          <Link
                            as="button"
                            color="indigo.500"
                            onClick={() => setDurationShown(true)}
                          >
                            set a duration
                          </Link>{' '}
                          instead.
                        </>
                      )}
                    </FormHelperText>
                  )}
                </>
              )}
            </FormControl>
          )}
          <FormControl>
            <FormLabel>Shoot date</FormLabel>
            <DatePicker minDetail="year" value={date} onChange={setDate} />
            {/* <Input type="date" name="date" defaultValue={project?.date} /> */}
          </FormControl>
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button onClick={onCancel} mr="3">
          Cancel
        </Button>
        <Button type="submit" colorScheme="pink" isLoading={loading}>
          {buttonText}
        </Button>
      </ModalFooter>
    </form>
  );
}

ProjectForm.propTypes = {
  isTrial: PropTypes.bool.isRequired,
  project: PropTypes.object,
  buttonText: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  mutation: PropTypes.object.isRequired,
  mutationOptions: PropTypes.object.isRequired
};
